import { intersection } from 'lodash/fp';
import { useState } from 'react';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import { NavbarItemData } from 'dashboard/components/Navbar/NavbarItem';
import { NavItem } from 'dashboard/models/role';
import { StyledCheckboxCell, StyledTableRow } from 'dashboard/views/DashboardRolesShowView/styles';
import Card from 'design-system/components/Card';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
} from 'design-system/components/Table';
import { Heading, Paragraph } from 'design-system/components/Text';
import useSidebarConfig, { SectionData } from 'lib/hooks/useSidebarConfig';

export interface NavbarTablesProps {
  disabled: boolean;
  shownRoleIsAdmin: boolean;
  enabledNavItems: NavItem[];
  readmeJwtToken: string;
  setEnabledNavItems: (ids: NavItem[]) => void;
}

const NavbarTables = ({
  disabled,
  shownRoleIsAdmin,
  enabledNavItems,
  readmeJwtToken,
  setEnabledNavItems,
}: NavbarTablesProps) => {
  const sections = useSidebarConfig({ readmeJwtToken });

  return (
    <>
      {sections.map((section) => (
        <NavbarSectionTable
          key={`navbar-table__section--${section.id}`}
          section={section}
          enabledNavItems={enabledNavItems}
          disabled={disabled}
          shownRoleIsAdmin={shownRoleIsAdmin}
          setEnabledNavItems={setEnabledNavItems}
        />
      ))}
    </>
  );
};

const NavbarSectionTable = ({
  disabled,
  shownRoleIsAdmin,
  enabledNavItems,
  section,
  setEnabledNavItems,
}: {
  disabled: boolean;
  shownRoleIsAdmin: boolean;
  enabledNavItems: NavItem[];
  section: SectionData;
  setEnabledNavItems: (ids: NavItem[]) => void;
}) => {
  const allLinks = section.items.flatMap((item) => item.links);
  const allVisibleLinks = allLinks.filter((link) => !link.hidden);

  if (allVisibleLinks.length === 0) {
    return null;
  }

  return (
    <Card>
      <Card.Body>
        <Card.Title>
          <Heading size='sm'>
            {section.label.charAt(0).toUpperCase() + section.label.toLowerCase().slice(1)}
          </Heading>
        </Card.Title>
        <Table>
          <TableHead>
            <StyledTableRow>
              <TableHeaderCell>
                <Paragraph color='primary'>Show</Paragraph>
              </TableHeaderCell>
              <StyledNameHeaderCell>
                <Spacer width={5} height={0} />
                <Paragraph color='primary'>Item</Paragraph>
              </StyledNameHeaderCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {section.items.map((item) => {
              return (
                <NavbarSectionRow
                  key={`navbar-table__section-row--${item.id}`}
                  item={item}
                  shownRoleIsAdmin={shownRoleIsAdmin}
                  enabledNavItems={enabledNavItems}
                  disabled={disabled}
                  setEnabledNavItems={setEnabledNavItems}
                />
              );
            })}
          </TableBody>
        </Table>
      </Card.Body>
    </Card>
  );
};

const NavbarSectionRow = ({
  item,
  enabledNavItems,
  shownRoleIsAdmin,
  disabled,
  setEnabledNavItems,
}: {
  item: NavbarItemData;
  enabledNavItems: NavItem[];
  shownRoleIsAdmin: boolean;
  disabled: boolean;
  setEnabledNavItems: (ids: NavItem[]) => void;
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  if (item.hidden) {
    return null;
  }

  const allVisibleLinks = item.links.filter((link) => !link.hidden);

  if (allVisibleLinks.length === 0) {
    return null;
  }

  /*
   * James: We want to support a single checkbox corresponding to a single nav
   * item. For example, the Team section currently has one nav item for all of
   * its subsections. I'm not sure if this is ideal, but I'm only concerned with
   * restoring the old behavior right now.
   */
  const mergedLabels: Record<NavItem | string, string[]> = {};
  allVisibleLinks.forEach((link) => {
    mergedLabels[link.navItem] ||= [];
    mergedLabels[link.navItem] = mergedLabels[link.navItem].concat(
      link.labelText || (link.label as string),
    );
  });
  const mergedNavItemOptions = Object.keys(mergedLabels);
  const hasAccordion = mergedNavItemOptions.length > 1;

  const allSelected =
    mergedNavItemOptions.length > 0 &&
    intersection(mergedNavItemOptions, enabledNavItems).length === mergedNavItemOptions.length;

  const someSelected = intersection(mergedNavItemOptions, enabledNavItems).length > 0;

  const toggleNavItems = (navItems: NavItem[]) => {
    if (enabledNavItems.some((navItem) => navItems.includes(navItem))) {
      setEnabledNavItems(enabledNavItems.filter((item) => !navItems.includes(item)));
    } else {
      setEnabledNavItems([...navItems, ...enabledNavItems]);
    }
  };

  return (
    <>
      <StyledTableRow hoverable={hasAccordion}>
        <StyledCheckboxCell>
          <StyledCheckbox
            id={`navbar-table__section-checkbox--${item.id}`}
            checked={shownRoleIsAdmin || allSelected}
            indeterminate={!allSelected && someSelected}
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleNavItems(item.links.map((link) => link.navItem));
            }}
          />
        </StyledCheckboxCell>
        <StyledNameCell
          id={`navbar-table__section-table-accordion-cell--${item.id}`}
          onClick={() => setExpanded(!expanded)}
        >
          {hasAccordion ? (
            <Accordion
              padded={false}
              caretSize='md'
              overrideExpanded={expanded}
              title={
                <StyledAccordionTitle>
                  <Paragraph color='primary'>{item.labelText || item.label}</Paragraph>
                </StyledAccordionTitle>
              }
            />
          ) : (
            <>
              <Spacer width={5} height={0} />
              <Paragraph color='primary'>{item.labelText || item.label}</Paragraph>
            </>
          )}
        </StyledNameCell>
      </StyledTableRow>
      {expanded &&
        hasAccordion &&
        Object.entries(mergedLabels).map((entry) => {
          const navItem = entry[0] as NavItem;
          const label = entry[1].join(', ');

          return (
            <StyledTableRow key={`navbar-table__link-row--${navItem}`} hoverable={false}>
              <StyledCheckboxCell>
                <StyledCheckbox
                  id={`navbar-table__link-checkbox--${navItem}`}
                  checked={shownRoleIsAdmin || enabledNavItems.includes(navItem)}
                  disabled={disabled}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleNavItems([navItem]);
                  }}
                />
              </StyledCheckboxCell>
              <StyledNameCell>
                <Spacer width={5} height={0} />
                <Paragraph color='primary'>{label}</Paragraph>
              </StyledNameCell>
            </StyledTableRow>
          );
        })}
    </>
  );
};

export default NavbarTables;

const StyledCheckbox = styled(Checkbox)`
  justify-content: center;
`;

const StyledNameHeaderCell = styled(TableHeaderCell)`
  display: flex;
`;

const StyledNameCell = styled(TableCell)`
  display: flex;
`;

const StyledAccordionTitle = styled.div`
  display: flex;
`;
